import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import onBehalfAction from 'redux/actions/onBehalfAction';
import deviceAction from 'redux/actions/deviceAction';
import { ACT_ON_BEHALF } from 'constants/localStorageConstants';
import UserService from 'services/userServices';
import { UserInfo } from 'models';
import { RootState } from 'redux/store';
import {
  GetSupportPath,
  GetSupportSearchPath,
  HomePath,
  KnowledgeDetail,
  ManageOrderPath,
  ManagePersonalRequestsPath,
  ManagePersonalTechnologyPath,
  ManageDevicesPath,
  MarketplaceCategoryPath,
  ManagePath,
  MarketplacePath,
  GetSupportLandingPath,
  GetTopKnowledgeArticlePath,
  ManageRequestTicketPath,
  ManageIncidentTicketPath,
  MarketplaceApprovalsPath,
} from 'utils/routes';
import { useMemo } from 'react';
import { NewJoinerOnBehalf } from 'pages/OnboardingPage/types';
import { getNewJoinerInfoByEmail, getLeaverInfoByEmail } from 'utils/function';
import { LeaverOnBehalf } from 'pages/OffboardingPage/types';
import isEmpty from 'lodash/isEmpty';

interface OnDispatchOnBehalfUserPayload {
  userOnBehalf: UserInfo | NewJoinerOnBehalf | null;
  isOnBehalf: boolean;
  isManager?: boolean;
  marketplaceAuth?: Record<string, any>;
  isOnBehalfNewJoiner?: boolean;
  isOnBehalfLeaver?: boolean;
}

interface OnEndOnBehalfOptions {
  navigateToHome?: boolean;
}

interface StorageData {
  enabled: boolean;
  email: string;
  isManager?: boolean;
  isNewjoiner?: boolean;
  isLeaver?: boolean;
  marketplaceAuth: Record<string, any> | null;
}

export interface IUseOnBehalf {
  onDispatchOnBehalfUser: (payload: OnDispatchOnBehalfUserPayload) => void;
  onGetUserDetailByEmail: (
    email: string,
    isNewjoiner: boolean,
    isLeaver: boolean
  ) => Promise<{ data: UserInfo | NewJoinerOnBehalf | LeaverOnBehalf; hasUser: boolean }>;
  onEndOnBehalf: (options?: OnEndOnBehalfOptions) => void;
  storageData: StorageData;
  shouldShowIndicator: boolean;
}

const PAGES_WITH_ABO = [
  ManagePersonalRequestsPath,
  ManagePersonalTechnologyPath,
  ManageOrderPath,
  GetSupportPath,
  GetSupportSearchPath,
  KnowledgeDetail,
  MarketplaceCategoryPath,
  HomePath,
  ManagePath,
  MarketplacePath,
  GetSupportLandingPath,
  GetTopKnowledgeArticlePath,
  MarketplaceApprovalsPath,
  ManageRequestTicketPath,
  ManageIncidentTicketPath
];

const getPagesWithABO = (array: string[]) => {
  const pagesWithABO = array.map(page => {
    if (page === KnowledgeDetail) {
      return KnowledgeDetail.replace(/:id/, "");
    }

    if (page === MarketplaceCategoryPath) {
      return MarketplaceCategoryPath.replace(/\*/g, "");
    }
    return page;
  });

  return pagesWithABO;
};

export const useOnBehalf = (): IUseOnBehalf => {
  const history = useHistory();
  const location = useLocation();

  const dispatch = useDispatch();
  const { isOnBehalf, isManager, userOnBehalf } = useSelector(
    (state: RootState) => state.onBehalfReducer,
  );

  const onDispatchOnBehalfUser = async (
    payload: OnDispatchOnBehalfUserPayload,
  ) => {
    const { userOnBehalf, isOnBehalf, isOnBehalfNewJoiner = false, isManager = false, isOnBehalfLeaver = false, } = payload;
    dispatch(onBehalfAction.setIsOnBehalfOf(isOnBehalf));
    dispatch(onBehalfAction.setIsOnBehalfOfNewJoiner(isOnBehalfNewJoiner));
    dispatch(onBehalfAction.setIsOnBehalfOfLeaver(isOnBehalfLeaver));
    dispatch(onBehalfAction.setUserOnBehalfOf(userOnBehalf));
    dispatch(onBehalfAction.setIsManager(isManager));
    dispatch(deviceAction.clearAllDevices());

    if (isOnBehalf) {
      dispatch(
        onBehalfAction.uptimeAuditLog({
          action: 'START',
        }),
      );
      const storageValue = JSON.stringify({
        enabled: isOnBehalf,
        email: userOnBehalf?.email,
        isNewjoiner: userOnBehalf?.new_joiner,
        isLeaver: userOnBehalf?.leaver,
        isManager,
      });
      localStorage.setItem(ACT_ON_BEHALF, storageValue);
    }
  };

  const onEndOnBehalf = (options: OnEndOnBehalfOptions = {}) => {
    const isManagerOnBehalf = isOnBehalf && isManager;

    window.history.replaceState({}, '');

    dispatch(
      onBehalfAction.uptimeAuditLog({
        action: 'END',
      }),
    );
    onDispatchOnBehalfUser({ userOnBehalf: null, isOnBehalf: false });
    localStorage.removeItem(ACT_ON_BEHALF);

    if (!!options.navigateToHome) {
      const path = isManagerOnBehalf ? ManageDevicesPath : HomePath;
      history.push(path);
    }
  };

  const onGetUserDetailByEmail = async (email: string, isNewjoiner: boolean, isLeaver: boolean) => {
    try {
      if (isNewjoiner) {
        const newJoiner = await getNewJoinerInfoByEmail(email);
        const userData = { ...newJoiner, new_joiner: isNewjoiner };
        return {
          data: userData,
          hasUser: !!userData.email,
        };
      }

      const { data: userArray } = await UserService.getUserDetailByEmail(email);
      const userDetail = userArray?.[0] || {};

      if (isLeaver) {
        const leaver = await getLeaverInfoByEmail(email);
        const userData = { ...userDetail, ...leaver, leaver: isLeaver };
        return {
          data: userData,
          hasUser: !!userData.email,
        };
      }

      return { data: userDetail, hasUser: !isEmpty(userDetail) };
    } catch (error) {
      return { data: null, hasUser: false };
    }
  };

  const storageData = useMemo((): StorageData => {
    const INITIAL_JSON_VALUE = `{ "enabled": false, "email": "" }`;
    const onBehalfData = localStorage.getItem(ACT_ON_BEHALF);
    return JSON.parse(onBehalfData || INITIAL_JSON_VALUE);
  }, [userOnBehalf]);

  const shouldShowIndicator = useMemo(() => {
    if (!isOnBehalf) return false;

    const pagesWithABO = getPagesWithABO(PAGES_WITH_ABO);

    const hasABOPage = pagesWithABO.some(page => {
      return location.pathname.startsWith(page);
    });
    return hasABOPage;
  }, [isOnBehalf, location.pathname]);

  return {
    onDispatchOnBehalfUser,
    onGetUserDetailByEmail,
    onEndOnBehalf,
    storageData,
    shouldShowIndicator,
  };
};
